import { ConsultationDetails, AgoraInfo } from "../libs/types";
import { safeStorage } from "./safeStorage";

type SessionState = {
  accessTokens: Record<string, string>;
  consultationInfo: ConsultationDetails | null;
  agoraInfo: AgoraInfo | null;
};

const state: SessionState = {
  accessTokens: {},
  consultationInfo: null,
  agoraInfo: null,
};

const parseJSON = <T>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

export const sessionStore = {
  hydrate(consultationId?: string) {
    if (consultationId && !state.accessTokens[consultationId]) {
      const stored = safeStorage.getSessionItem(
        `access_token_${consultationId}`,
      );
      if (stored) state.accessTokens[consultationId] = stored;
    }
    if (!state.consultationInfo) {
      state.consultationInfo = parseJSON<ConsultationDetails>(
        safeStorage.getSessionItem("consultationInfo"),
      );
    }
    if (!state.agoraInfo) {
      state.agoraInfo = parseJSON<AgoraInfo>(
        safeStorage.getSessionItem("agoraInfo"),
      );
    }
  },
  getAccessToken(consultationId: string) {
    this.hydrate(consultationId);
    return state.accessTokens[consultationId] ?? null;
  },
  setAccessToken(consultationId: string, token: string | null) {
    if (!consultationId) return;
    if (token) {
      state.accessTokens[consultationId] = token;
      safeStorage.setSessionItem(`access_token_${consultationId}`, token);
    } else {
      delete state.accessTokens[consultationId];
      safeStorage.removeSessionItem(`access_token_${consultationId}`);
    }
  },
  getConsultationInfo() {
    this.hydrate();
    return state.consultationInfo;
  },
  setConsultationInfo(info: ConsultationDetails | null) {
    state.consultationInfo = info;
    if (info) {
      safeStorage.setSessionItem("consultationInfo", JSON.stringify(info));
    } else {
      safeStorage.removeSessionItem("consultationInfo");
    }
  },
  getAgoraInfo() {
    this.hydrate();
    return state.agoraInfo;
  },
  setAgoraInfo(info: AgoraInfo | null) {
    state.agoraInfo = info;
    if (info) {
      safeStorage.setSessionItem("agoraInfo", JSON.stringify(info));
    } else {
      safeStorage.removeSessionItem("agoraInfo");
    }
  },
  clearAuth(consultationId?: string) {
    if (consultationId) {
      delete state.accessTokens[consultationId];
      safeStorage.removeSessionItem(`access_token_${consultationId}`);
    }
  },
};
